import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { CreateComposer } from "../create/CreateComposer";
import { assetUrl } from "../../lib/assets";

export interface RemixSource {
	playableId: string;
	title?: string;
}

let listener: ((source: RemixSource) => void) | null = null;

export function openRemixPanel(source: RemixSource) {
	listener?.(source);
}

export const RemixPanel = () => {
	const { t } = useTranslation();
	const [source, setSource] = useState<RemixSource | null>(null);

	useEffect(() => {
		listener = (next) => {
			setSource(next);
		};
		return () => {
			listener = null;
		};
	}, []);

	useEffect(() => {
		if (!source) return;
		function handleKeyDown(event: KeyboardEvent) {
			if (event.key === "Escape") setSource(null);
		}
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [source]);

	if (!source) return null;

	return (
		<div className="remix-panel-layer fixed inset-0 z-[60]">
			<button
				type="button"
				onClick={() => setSource(null)}
				className="absolute inset-0 size-full cursor-default bg-black/10"
				aria-label={t("common.close")}
			/>
			<section
				role="dialog"
				aria-modal="true"
				aria-label={t("remix.title", { title: source.title || t("remix.untitled") })}
				className="remix-floating-panel absolute bottom-[4.75rem] left-3 right-3 mx-auto w-auto max-w-[34rem] animate-sheet-up overflow-hidden rounded-[20px] border border-white/15 bg-[#141414]/98 shadow-[0_24px_70px_rgba(0,0,0,0.62)] lg:bottom-[5.5rem] lg:left-auto lg:right-5 lg:mx-0 lg:w-[min(34rem,calc(100vw-2.5rem))]"
			>
				<header className="remix-panel-header flex min-h-14 items-center justify-between gap-3 border-b border-white/10 px-4 py-2.5">
					<div className="flex min-w-0 items-center gap-2.5 text-white">
						<img
							src={assetUrl("brand/haha-mascot.png")}
							alt=""
							draggable={false}
							className="size-5 shrink-0 object-contain"
						/>
						<h2 className="truncate text-sm font-bold sm:text-base">{t("remix.promptTitle")}</h2>
					</div>
					<button
						type="button"
						onClick={() => setSource(null)}
						className="remix-panel-close grid size-8 shrink-0 place-items-center rounded-full bg-white/8 text-white/55 transition hover:bg-white/14 hover:text-white"
						aria-label={t("common.close")}
					>
						<X size={18} />
					</button>
				</header>
				<CreateComposer
					variant="home"
					remixOf={source.playableId}
					remixSource={source}
					placeholder={t("remix.placeholder")}
					autoFocus
					embedded
				/>
			</section>
		</div>
	);
};
